export interface MetaEmbeddedSignupConfiguration {
    appId: string;
    configId: string;
}

export interface MetaEmbeddedSignupResult {
    code: string;
    wabaId: string;
    phoneNumberId?: string;
}

/**
 * - EXISTING_WABA: shares a WABA without Meta's "add a phone number" step, so
 *   WABAs and numbers that already exist can be brought in; each number is
 *   then activated in the Portal (/register with PIN).
 * - NEW_NUMBER: full flow, where the customer creates the WABA and verifies a
 *   new number inside Meta's window.
 */
export type MetaSignupMode = 'EXISTING_WABA' | 'NEW_NUMBER';

export interface MetaSdk {
    init: (configuration: { appId: string; version: string }) => void;
    login: (
        callback: (response: { authResponse?: { code?: string } }) => void,
        options: Record<string, unknown>,
    ) => void;
}

declare global {
    interface Window {
        FB?: MetaSdk;
    }
}

const META_SDK_ID = 'meta-jssdk';
const META_SIGNUP_TIMEOUT_MS = 10 * 60 * 1000;
const META_ORIGINS = new Set(['https://www.facebook.com', 'https://web.facebook.com']);
const META_SIGNUP_FINISH_EVENTS = new Set([
    'FINISH',
    'FINISH_ONLY_WABA',
    'FINISH_WHATSAPP_BUSINESS_APP_ONBOARDING',
]);

export const loadMetaSdk = (appId: string): Promise<MetaSdk> =>
    new Promise((resolve, reject) => {
        if (window.FB) {
            window.FB.init({ appId, version: 'v21.0' });
            resolve(window.FB);
            return;
        }

        const script = document.createElement('script');
        script.id = META_SDK_ID;
        script.async = true;
        script.src = 'https://connect.facebook.net/pt_BR/sdk.js';
        script.onload = () => {
            if (!window.FB) return reject(new Error('META_SDK_UNAVAILABLE'));
            window.FB.init({ appId, version: 'v21.0' });
            resolve(window.FB);
        };
        script.onerror = () => reject(new Error('META_SDK_UNAVAILABLE'));
        document.head.appendChild(script);
    });

export const launchMetaEmbeddedSignup = (
    sdk: MetaSdk,
    configId: string,
    mode: MetaSignupMode,
): Promise<MetaEmbeddedSignupResult> =>
    new Promise((resolve, reject) => {
        let code: string | null = null;
        let wabaId: string | null = null;
        let phoneNumberId: string | null = null;

        const timeout = window.setTimeout(
            () => finishWithError(new Error('META_SIGNUP_TIMEOUT')),
            META_SIGNUP_TIMEOUT_MS,
        );
        const cleanup = () => {
            window.clearTimeout(timeout);
            window.removeEventListener('message', onMessage);
        };
        const finishWithError = (error: Error) => {
            cleanup();
            reject(error);
        };
        const finishWhenReady = () => {
            if (!code || !wabaId) return;
            cleanup();
            resolve({ code, wabaId, phoneNumberId: phoneNumberId || undefined });
        };
        const onMessage = (event: MessageEvent<unknown>) => {
            if (!META_ORIGINS.has(event.origin)) return;
            let payload: unknown = event.data;
            if (typeof event.data === 'string') {
                try {
                    payload = JSON.parse(event.data);
                } catch {
                    return;
                }
            }
            if (!payload || typeof payload !== 'object') return;
            const data = payload as {
                type?: string;
                event?: string;
                data?: Record<string, string>;
            };
            if (data.type !== 'WA_EMBEDDED_SIGNUP') return;
            if (data.event === 'CANCEL' || data.event === 'ERROR') {
                return finishWithError(new Error('META_SIGNUP_CANCELLED'));
            }
            if (!META_SIGNUP_FINISH_EVENTS.has(data.event || '')) return;
            wabaId = data.data?.waba_id || null;
            phoneNumberId = data.data?.phone_number_id || null;
            finishWhenReady();
        };

        window.addEventListener('message', onMessage);
        try {
            sdk.login(
                (response) => {
                    code = response.authResponse?.code || null;
                    if (!code) return finishWithError(new Error('META_SIGNUP_CANCELLED'));
                    finishWhenReady();
                },
                {
                    config_id: configId,
                    response_type: 'code',
                    override_default_response_type: true,
                    // Coexistence ('whatsapp_business_app_onboarding') comes later.
                    extras: {
                        setup: {},
                        ...(mode === 'EXISTING_WABA' && { featureType: 'only_waba_sharing' }),
                        sessionInfoVersion: '3',
                    },
                },
            );
        } catch {
            finishWithError(new Error('META_SIGNUP_UNAVAILABLE'));
        }
    });
